import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { logProjectEvent } from "@/hooks/use-project-events";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { DBInvoice } from "@/lib/db-types";

export interface ManualRevenueInput {
  project_id: string;
  /** Beløp eks. mva. Negative beløp (kreditjustering) er tillatt. */
  amount_excl_vat: number;
  /** ISO-dato (yyyy-MM-dd). */
  invoice_date: string;
  description: string;
}

export interface ManualRevenueUpdate extends ManualRevenueInput {
  invoice_id: string;
  /** Forrige beløp — kun til audit-payload (old → new). */
  old_amount_excl_vat: number | null;
}

/**
 * Mutations for manuell inntektsregistrering (VIB-792, modell B): rader i
 * `invoices` med is_manual = true, poweroffice_invoice_id = NULL og
 * status = 'paid' (teller dermed i BF-/faktureringsstatus-regelen
 * sent/paid/overdue/credit_note uten KPI-kodeendring; recompute_project_bf
 * fyrer på insert/update/delete). RLS begrenser skriving til admin.
 *
 * Audit (økonomi-event): aktør er ALLTID den faktisk innloggede brukeren
 * (useAuth), med on_behalf_of i payload ved impersonering. Ved delete logger
 * frontend full snapshot i payload, i tillegg til DB-triggerens
 * to_jsonb(OLD)-snapshot i event_log.
 */
export function useManualRevenue(projectId: string) {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();
  const { activeUser, isImpersonating } = useImpersonation();

  const auditBase = () => ({
    projectId,
    userId: authUser?.id ?? null,
  });
  const withActorContext = (payload: Record<string, unknown>) => ({
    ...payload,
    ...(isImpersonating && activeUser ? { on_behalf_of: activeUser.id } : {}),
  });

  const invalidate = async () => {
    // Raden bor i invoices; recompute-triggeren oppdaterer projects.bf_*.
    await invalidateFor(qc, "invoices", { projectId });
    await invalidateFor(qc, "projects", { projectId });
  };

  const addManualRevenue = useMutation({
    mutationFn: async (input: ManualRevenueInput): Promise<DBInvoice> => {
      const { data, error } = await supabase
        .from("invoices")
        .insert({
          project_id: input.project_id,
          is_manual: true,
          poweroffice_invoice_id: null,
          status: "paid",
          amount: input.amount_excl_vat,
          amount_excl_vat: input.amount_excl_vat,
          invoice_date: input.invoice_date,
          description: input.description,
        })
        .select()
        .single();
      if (error) throw error;
      return data as DBInvoice;
    },
    onSuccess: async (row, input) => {
      await logProjectEvent({
        ...auditBase(),
        eventType: "manual_revenue_added",
        entityType: "invoice",
        entityId: row.id,
        payload: withActorContext({
          amount_excl_vat: input.amount_excl_vat,
          invoice_date: input.invoice_date,
          description: input.description,
          project_id: input.project_id,
        }),
      });
      toast.success("Manuell inntekt registrert");
    },
    onError: (err: Error) => toast.error(`Kunne ikke registrere inntekt: ${err.message}`),
    onSettled: invalidate,
  });

  const updateManualRevenue = useMutation({
    mutationFn: async (input: ManualRevenueUpdate): Promise<DBInvoice> => {
      const { data, error } = await supabase
        .from("invoices")
        .update({
          amount: input.amount_excl_vat,
          amount_excl_vat: input.amount_excl_vat,
          invoice_date: input.invoice_date,
          description: input.description,
        })
        .eq("id", input.invoice_id)
        .eq("is_manual", true)
        .select()
        .single();
      if (error) throw error;
      return data as DBInvoice;
    },
    onSuccess: async (row, input) => {
      await logProjectEvent({
        ...auditBase(),
        eventType: "manual_revenue_updated",
        entityType: "invoice",
        entityId: row.id,
        payload: withActorContext({
          amount_excl_vat: input.amount_excl_vat,
          old_amount_excl_vat: input.old_amount_excl_vat,
          invoice_date: input.invoice_date,
          description: input.description,
          project_id: input.project_id,
        }),
      });
      toast.success("Manuell inntekt oppdatert");
    },
    onError: (err: Error) => toast.error(`Kunne ikke oppdatere inntekt: ${err.message}`),
    onSettled: invalidate,
  });

  const deleteManualRevenue = useMutation({
    mutationFn: async (row: DBInvoice): Promise<DBInvoice> => {
      const { error } = await supabase
        .from("invoices")
        .delete()
        .eq("id", row.id)
        .eq("is_manual", true);
      if (error) throw error;
      return row;
    },
    onSuccess: async (row) => {
      // Full snapshot i payload (audit-regel: aldri null payload ved delete).
      await logProjectEvent({
        ...auditBase(),
        eventType: "manual_revenue_deleted",
        entityType: "invoice",
        entityId: row.id,
        payload: withActorContext({ old: row as unknown as Record<string, unknown>, project_id: projectId }),
      });
      toast.success("Manuell inntekt slettet");
    },
    onError: (err: Error) => toast.error(`Kunne ikke slette inntekt: ${err.message}`),
    onSettled: invalidate,
  });

  return { addManualRevenue, updateManualRevenue, deleteManualRevenue };
}
